"use client";

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import Image from "next/image";
import { BrochureSection } from "@/types/dto/landing-page/landing-page";
import DynamicCtaButton from "@/components/common/dynamic-cta-button/dynamic-cta-button";
import { dynamicCtaButtonFactory } from "@/factories/common/dynamic-cta-button/dynamic-cta-button-factory";
import useScrollToAnchor from "@/hooks/scroll-to-anchor/use-scroll-to-anchor";

interface BottomCtaSectionProps extends BrochureSection {
  moduleRankingPosition?: string;
}

const BottomCtaSection = ({
  basicInfo,
  brochureImage,
  anchorId,
  moduleRankingPosition = "7"
}: BottomCtaSectionProps) => {
  
  const { ctas } = basicInfo ?? {};

  const dynamicCta = ctas?.[0] ? dynamicCtaButtonFactory(ctas[0]) : undefined;

  useScrollToAnchor(anchorId);

  return (
    <Box
      component="section"
      className="destination-page-container"
      id={anchorId}
      sx={{
        padding: {
          xs: "4rem 2.4rem",
          md: "4rem 2.4rem",
          lg: "6rem 10rem"
        }
      }}
      data-testid="bottom-cta-container"
      data-track="visibility"
      data-parent-container-type="brochure form ctas"
      data-module-name="Bottom Cta Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position={moduleRankingPosition}
    >
      <Box
        sx={{
          width: "100%",
          overflow: "hidden",
          position: "relative",
          borderRadius: "20px",
          maxWidth: "124rem",
          alignItems: "center",
          boxShadow: "0 3px 6px 0 rgba(0, 0, 0, 0.10)",
          "&::before": {
            content: "\"\"",
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.00) 60%)",
            zIndex: 1,
            pointerEvents: "none"
          }
        }}
      >
        <Image
          src={brochureImage.imageUrl ?? ""}
          alt={brochureImage.altText ?? ""}
          fill
          style={{
            objectFit: "cover",
            objectPosition: "center",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 0
          }}
        />
        <Stack
          sx={{
            padding: "4rem",
            flexDirection: { xs: "column", md: "row" },
            justifyContent:"space-between",
            alignItems: "center",
            minHeight: {xs:"39rem", md:"30rem", lg:"31rem"},
            gap: "0.6rem",
            position: "relative",
            zIndex: 2
          }}
        >
          <Stack
            sx={{
              gap: "0.6rem",
              maxWidth: {md:"29rem", lg:"52rem"}
            }}
          >
            <Typography variant="h4" color="common.white" data-testid="bottom-cta-header">
              {basicInfo.headerText}
            </Typography>
            <Box
              sx={{
                color: "common.white",
                fontSize: "1.8rem",
                fontWeight: 500,
                lineHeight: "3rem",
                margin: "0 !important"
              }}
              dangerouslySetInnerHTML={{
                __html: basicInfo.copyText ?? ""
              }}
              data-testid="bottom-cta-subtext"
            />
          </Stack>
          {dynamicCta && (
            <DynamicCtaButton
              label={dynamicCta.label ?? ""}
              ctaType={dynamicCta.ctaType}
              ctaData={dynamicCta.ctaData}
              testId="bottom-cta-section"
              variant={dynamicCta.variant ?? "outlined"}
              color={dynamicCta.color ?? "primary"}
              targetIdSuffix="brochure-cta-section"
            />
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default BottomCtaSection;
